import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Table, Dialog, Input, Select, Alert, Badge, useToast } from '@dreamtree-org/twreact-ui';
import { Plus, Pencil, PackagePlus } from 'lucide-react';
import { PageHeader, QueryState, EmptyState, StatusBadge } from '@/components';
import { errorText, formatINR } from '@/lib/format';
import { useQuery, useMutation } from '@/lib/useQuery';
import { useProductTypes } from '@/lib/productTypes';
import SpecFields from '@/merchant/components/SpecFields';
import StatusTabs from '@/merchant/components/StatusTabs';
import { PRODUCT_TABS } from '@/merchant/lib/vendors';
import { emptyForm, validateForm, pickedCategory, createPayload, editPatch, needsReview } from '../lib/productForm';
import { conditionOptions, gradeOptions, gradeRequired } from '@/lib/conditions';

/**
 * My products (B17, D80). Add a product under a category › subcategory with
 * its type's specs; the store reviews it before it goes on sale. Stock changes
 * keep a live product live; any other edit sends it back to review.
 */
export default function Products() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState('');
  const list = useQuery('vendorProducts', tab ? { review_status: tab } : {}, { select: (r) => r.data || [] });
  const tree = useQuery('vendorCategoryTree', undefined, { select: (r) => r.data || [] });
  const types = useProductTypes();
  const createM = useMutation('vendorCreateProduct');
  const updateM = useMutation('vendorUpdateProduct');
  const [editing, setEditing] = useState(null); // { product|null, form }
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  const categories = tree.data || [];
  const typeOf = (key) => types.byKey[key] || null;
  const open = (product = null) => { setError(''); setTouched(false); setEditing({ product, form: emptyForm(categories, product, typeOf) }); };

  // /products?new=1 (from the overview) opens the form once the categories are in.
  useEffect(() => {
    if (params.get('new') && tree.data && !types.loading) {
      open();
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, tree.data, types.loading]);

  const form = editing && editing.form;
  const category = form ? pickedCategory(categories, form) : null;
  const type = category ? typeOf(category.product_type) : null;
  const errors = form ? validateForm(form, type, types.conditions) : {};
  const set = (patch) => setEditing((e) => ({ ...e, form: { ...e.form, ...patch } }));
  const topOf = categories.find((t) => String(t.id) === String(form && form.top));

  const save = async () => {
    setTouched(true);
    if (Object.keys(errors).length) return;
    setError('');
    try {
      if (!editing.product) {
        await createM.run(createPayload(form, category, type, types.conditions));
        toast.success('Product sent for review', form.title);
      } else {
        const patch = editPatch(editing.product, form, category, type, types.conditions);
        if (Object.keys(patch).length === 1) { setEditing(null); return; }
        await updateM.run(patch);
        toast.success(needsReview(patch) ? 'Saved — sent for review again' : 'Stock updated', form.title);
      }
      setEditing(null);
      list.reload();
    } catch (ex) {
      setError(errorText(ex));
    }
  };

  const reviewHint = editing && editing.product && editing.product.live && form && needsReview(editPatch(editing.product, form, category, type, types.conditions))
    ? 'This product is live. Saving these changes takes it off the storefront until the store approves it again. Changing only the stock keeps it live.'
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="My products"
        subtitle="Products you sell on this store. Each one is reviewed by the store before customers can buy it."
        actions={<Button leftIcon={<Plus size={16} />} onClick={() => open()} disabled={!tree.data}>Add product</Button>}
      />
      <StatusTabs tabs={[{ key: '', label: 'All' }, ...PRODUCT_TABS.filter((t) => t.key)]} value={tab} onChange={setTab} />
      <QueryState query={list} skeleton="table">
        {(rows) => (rows && rows.length ? (
          <Table
            data={rows}
            columns={[
              {
                key: 'title', label: 'Product',
                render: (p) => (
                  <span>
                    <span className="block font-medium">{p.title}</span>
                    <span className="block text-xs opacity-70">{[p.category && p.category.name, p.subcategory && p.subcategory.name].filter(Boolean).join(' › ')} · {p.sku}</span>
                    {p.review_status === 'rejected' && p.review_note ? <span className="block text-xs text-danger-700">Store’s note: {p.review_note}</span> : null}
                  </span>
                ),
              },
              { key: 'price_paise', label: 'Price', render: (p) => formatINR(p.price_paise) },
              { key: 'qty_available', label: 'Stock', render: (p) => `${p.qty_available}${p.qty_on_hand !== p.qty_available ? ` (${p.qty_on_hand - p.qty_available} held)` : ''}` },
              { key: 'review_status', label: 'Status', render: (p) => (p.live ? <Badge size="sm" variant="success">Live</Badge> : <StatusBadge status={p.review_status} label={p.review_status === 'pending' ? 'In review' : undefined} />) },
            ]}
            actions={[{ name: 'edit', label: 'Edit', icon: <Pencil size={14} />, inline: true, onClick: ({ row }) => open(row) }]}
            replaceDefaultActions
            withAction
            showSerial={false}
            showColumnPicker={false}
            globalSearch
            pagination
            pageSize={25}
          />
        ) : (
          <EmptyState title="No products yet" icon={<PackagePlus size={28} />}>Add your first product. Pick its category and subcategory, fill in the specs, and the store will review it.</EmptyState>
        ))}
      </QueryState>

      <Dialog isOpen={!!editing} onClose={() => setEditing(null)} title={editing && editing.product ? 'Edit product' : 'Add a product'} size="lg">
        {form ? (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save(); }}>
            {!categories.length ? <Alert variant="warning" title="No categories yet">The store has not set up any categories to sell in. Ask the store to add them.</Alert> : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="Category"
                options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
                value={form.top}
                onChange={(v) => set({ top: v || '', sub: '', specs: {} })}
                required
                error={touched ? errors.top : undefined}
              />
              <Select
                label="Subcategory"
                options={(topOf ? topOf.children : []).map((c) => ({ value: String(c.id), label: c.name }))}
                value={form.sub}
                onChange={(v) => set({ sub: v || '' })}
                disabled={!topOf || !topOf.children.length}
                helperText={topOf && !topOf.children.length ? 'This category has no subcategories.' : undefined}
              />
              <Input label="Brand" value={form.brand} onChange={(e) => set({ brand: e.target.value })} required maxLength={64} error={touched ? errors.brand : undefined} />
              <Input label="Model" value={form.model_name} onChange={(e) => set({ model_name: e.target.value })} required maxLength={160} error={touched ? errors.model_name : undefined} />
            </div>
            <Input label="Title customers see" value={form.title} onChange={(e) => set({ title: e.target.value })} required maxLength={255} error={touched ? errors.title : undefined} />
            <div className="grid gap-3 sm:grid-cols-3">
              <Input label="Selling price (₹)" inputMode="decimal" value={form.price} onChange={(e) => set({ price: e.target.value })} required error={touched ? errors.price : undefined} />
              <Input label="MRP (₹)" inputMode="decimal" value={form.mrp} onChange={(e) => set({ mrp: e.target.value })} helperText="Optional" error={errors.mrp} />
              <Input label="Stock (quantity)" inputMode="numeric" value={form.qty} onChange={(e) => set({ qty: e.target.value })} error={errors.qty} />
              <Select label="Condition" options={conditionOptions(types.conditions, type)} value={form.condition} onChange={(v) => set({ condition: v || '' })} required error={touched ? errors.condition : undefined} />
              {gradeRequired(types.conditions, form.condition) ? (
                <Select label="Grade" options={gradeOptions()} value={form.grade} onChange={(v) => set({ grade: v || '' })} required error={touched ? errors.grade : undefined} />
              ) : null}
              <Input label="Warranty (months)" inputMode="numeric" value={form.warranty_months} onChange={(e) => set({ warranty_months: e.target.value })} error={errors.warranty_months} />
            </div>
            {type ? (
              <div className="space-y-2">
                <h3 className="font-semibold">{type.label} specifications</h3>
                <SpecFields type={type} value={form.specs} onChange={(patch) => set({ specs: { ...form.specs, ...patch } })} errors={touched ? errors.specs || {} : {}} />
              </div>
            ) : null}
            {reviewHint ? <Alert variant="warning" title="Goes back to review">{reviewHint}</Alert> : null}
            {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" loading={createM.loading || updateM.loading}>{editing.product ? 'Save' : 'Send for review'}</Button>
            </div>
          </form>
        ) : null}
      </Dialog>
    </div>
  );
}
